import React, { useState } from 'react';
import {
    Box,
    Typography,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Chip,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import MemoryIcon from '@mui/icons-material/Memory';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_BASE_URL } from '../../../config/config';

dayjs.extend(relativeTime);

// Modern iOS-style Switch
const IOSSwitch = styled(Switch)(({ theme }) => ({
    width: 48,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(24px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#34c759',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
        boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
    },
    '& .MuiSwitch-track': {
        borderRadius: 24 / 2,
        backgroundColor: '#e9e9ea',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const SequencerStatusBox = ({ motor, deviceCode, onRefresh, allVirtualMotors }) => {
    const [loading, setLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Get only the other motors that are ON to show in the chips
    const activeMotors = allVirtualMotors?.filter(
        (m) => m.status === 'ON' && m._id !== motor._id
    ) || [];

    const getTimeDisplay = () => {
        if (!motor.lastCommand?.timestamp) return '';
        const lastTime = dayjs(motor.lastCommand.timestamp);
        const now = dayjs();
        const totalMinutes = now.diff(lastTime, 'minute');
        if (totalMinutes < 1) return '';
        if (totalMinutes < 60) return `for ${String(totalMinutes).padStart(2, '0')} Min`;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `for ${String(hours).padStart(2, '0')} Hr ${String(minutes).padStart(2, '0')} Min`;
    };

    // Determine colors based on status
    const getTheme = () => {
        if (motor.sumpError || motor.flowError || motor.pressureError || motor.timeoutError) {
            return { border: '#fca5a5', leftAccent: '#ef4444', text: '#b91c1c', label: 'ERROR DETECTED' };
        }
        switch (motor.status) {
            case 'ON': return { border: '#e2e8f0', leftAccent: '#22c55e', text: '#15803d', label: 'SEQUENCE RUNNING' };
            case 'PLEASE_WAIT': return { border: '#e2e8f0', leftAccent: '#3b82f6', text: '#1d4ed8', label: 'WAITING...' };
            case 'OFF':
            default: return { border: '#e2e8f0', leftAccent: '#94a3b8', text: '#64748b', label: 'SEQUENCE OFF' };
        }
    };

    const themeVars = getTheme();

    const isControlDisabled = loading ||
        motor.sumpError ||
        motor.flowError ||
        motor.pressureError ||
        motor.timeoutError ||
        motor.status === 'PLEASE_WAIT';

    const handleToggle = (checked) => {
        const action = checked ? 'ON' : 'OFF';
        setPendingAction(action);
        setConfirmDialog(true);
    };

    const handleConfirm = async () => {
        setConfirmDialog(false);
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE_URL}api/virtual/motor/${deviceCode}/${motor._id}/execute`,
                { action: pendingAction },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({ open: true, message: `Logic ${pendingAction} command sent`, severity: 'success' });
            setTimeout(() => { if (onRefresh) onRefresh(); }, 1500);
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Action failed', severity: 'error' });
        } finally {
            setLoading(false);
            setPendingAction(null);
        }
    };

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    width: '100%',
                    borderRadius: 3,
                    border: `1px solid ${themeVars.border}`,
                    borderLeft: `6px solid ${themeVars.leftAccent}`,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                    }
                }}
            >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>

                    {/* Header: Icon, Name, Status, and Toggle */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box display="flex" alignItems="flex-start" gap={1.2} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ mt: 0.2, flexShrink: 0 }}>
                                <SettingsSuggestIcon sx={{ color: themeVars.leftAccent, fontSize: 26 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#1e293b',
                                        fontSize: { xs: '1rem', sm: '1.1rem' },
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {motor.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: themeVars.text,
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        mt: 0.5,
                                        display: 'block',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {themeVars.label}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Switch Control */}
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', height: 28 }}>
                            {loading ? (
                                <CircularProgress size={22} sx={{ color: themeVars.leftAccent }} />
                            ) : (
                                <IOSSwitch
                                    checked={motor.status === 'ON'}
                                    onChange={(e) => handleToggle(e.target.checked)}
                                    disabled={isControlDisabled}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Middle: Time Info */}
                    <Box display="flex" alignItems="flex-start" gap={0.8} sx={{ mt: 1.5, ml: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: '#94a3b8', flexShrink: 0, mt: '2px' }} />
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 500, lineHeight: 1.4 }}>
                            {motor.lastCommand?.timestamp ? (
                                <>
                                    {dayjs(motor.lastCommand.timestamp).format('h:mm A, D MMM')}
                                    {getTimeDisplay() && <span style={{ opacity: 0.8 }}> • {getTimeDisplay()}</span>}
                                </>
                            ) : (
                                <span style={{ fontStyle: 'italic' }}>No recent activity</span>
                            )}
                        </Typography>
                    </Box>

                    {/* Bottom: Dedicated Active Hardware Container (ONLY VISIBLE IF THERE ARE ACTIVE MOTORS) */}
                    {activeMotors.length > 0 && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                border: '1px solid #f1f5f9'
                            }}
                        >
                            <Stack direction="row" alignItems="center" gap={0.8} mb={1}>
                                <MemoryIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Active Motors
                                </Typography>
                            </Stack>

                            <Box display="flex" flexWrap="wrap" gap={0.8}>
                                {activeMotors.map((m) => (
                                    <Chip
                                        key={m._id}
                                        label={m.name}
                                        size="small"
                                        sx={{
                                            bgcolor: '#dcfce7',
                                            color: '#166534',
                                            fontWeight: 700,
                                            fontSize: '0.7rem',
                                            height: 22,
                                            border: '1px solid #bbf7d0'
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>Confirm Action</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to turn <strong>{motor.name}</strong> {pendingAction}?</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disableElevation
                        sx={{
                            bgcolor: pendingAction === 'ON' ? '#22c55e' : '#f97316',
                            fontWeight: 700,
                            '&:hover': { bgcolor: pendingAction === 'ON' ? '#16a34a' : '#ea580c' }
                        }}
                    >
                        Confirm {pendingAction}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SequencerStatusBox;
